import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';

import { CartContext } from '../../contexts/CartContext';
import { TmAddress } from '../../tscapi/model/account';
import { TmSelectShippingAddressRequest } from '../../tscapi/model/request/account_cart-request';
import { updateAccountBillingAddress, updateAccountShippingAddress } from '../../tscapi/service/account.service';
import { AccountUtils } from '../../tscapi/utils/account-utils';

import { AddressForm } from './address.form';
import { Loading } from './loading.component';

interface EditModalProps {
    openEditModal: boolean;
    setEditModalIsOpen: (boolean) => void;
    selectedAddress: TmAddress;
    setSelectedAddress: (TmAddress) => void;
    setOpenParentModal: (boolean) => void;
}

export const ShippingAddressEdit = ({
    openEditModal,
    setEditModalIsOpen,
    selectedAddress,
    setSelectedAddress,
    setOpenParentModal,
}: EditModalProps) => {
    const { t } = useTranslation();
    const { cartData, getCart, updateShippingAddress, setIsLoading } = useContext(CartContext);
    const [addressErrorMessage, setErrorMessage] = useState('');
    const [formState, setFormState] = useState('');

    const closeModal = () => {
        setErrorMessage('');
        setEditModalIsOpen(false);
        setOpenParentModal(true);
    };
    const editAddress = async () => {
        if (selectedAddress) {
            try {
                setIsLoading(true);
                const cartAddressToEdit = AccountUtils.formatAddressData(selectedAddress);

                const shipAddressReq = new TmSelectShippingAddressRequest();
                let accountResponse = null;
                if (cartAddressToEdit.Id == cartData.Buyer.BillingAddress.Id) {
                    accountResponse = await updateAccountBillingAddress(cartAddressToEdit);
                } else {
                    accountResponse = await updateAccountShippingAddress(cartAddressToEdit);
                }
                if (
                    accountResponse &&
                    accountResponse.data &&
                    cartData.ShippingAddress &&
                    cartData.ShippingAddress.Id == cartAddressToEdit.Id
                ) {
                    try {
                        shipAddressReq.ShippingAddressId = cartAddressToEdit.Id;
                        await updateShippingAddress(shipAddressReq);
                        closeModal();
                    } catch (error) {
                        setErrorMessage(
                            AccountUtils.getErrorMessage(
                                error,
                                t('Unable to update your shipping address. Please try again.'),
                            ),
                        );
                    }
                } else {
                    await getCart();
                    closeModal();
                }
            } catch (error) {
                console.log(error);
                setErrorMessage(
                    AccountUtils.getErrorMessage(error, t('Unable to update your shipping address. Please try again.')),
                );
            } finally {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (formState == 'submit') {
            editAddress();
        }
    }, [formState]);

    const validateFormAndSubmit = () => {
        setFormState('validate');
    };

    Modal.setAppElement('#expressCheckout');
    return (
        <>
            <div>
                <Modal
                    isOpen={openEditModal}
                    contentLabel={t('Edit Shipping Address')}
                    className="modal__contentwrap"
                    overlayClassName="modal__overlay"
                >
                    <div className="modal__header">
                        <button className="modal__button-back" onClick={closeModal}>
                            {t('Back')}
                        </button>
                        <h3>{t('Edit Shipping Address')}</h3>
                        <button className="modal__button-close" onClick={closeModal}>
                            {t('Close')}
                        </button>
                    </div>
                    <div className="modal__body">
                        {addressErrorMessage && (
                            <div className="alert alert-danger">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    className="form__error-svg"
                                >
                                    <path d="M0 0h24v24H0z" fill="none"></path>
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
                                </svg>
                                {addressErrorMessage}
                            </div>
                        )}
                        <AddressForm
                            selectedAddress={selectedAddress}
                            setSelectedAddress={setSelectedAddress}
                            formState={formState}
                            setFormState={setFormState}
                            setErrorMessage={setErrorMessage}
                        />
                    </div>
                    <div className="modal__footer">
                        <Loading />
                        <button className="modal__button--save button" onClick={validateFormAndSubmit}>
                            {t('Save & Continue')}
                        </button>
                    </div>
                </Modal>
            </div>
        </>
    );
};
